import { AlertTriangle, Check, type LucideIcon } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils/cn';

export interface ModuleSelectorOption {
  value: string;
  label: string;
  description?: string;
  icon?: LucideIcon;
}

interface ModuleSelectorProps {
  options: ModuleSelectorOption[];
  value: string[];
  onChange: (next: string[]) => void;
  /**
   * Render extra fields inside the card when this module is selected.
   * Use for things like "TTS plan" inside the TTS module or
   * "Access key / Secret key" inside the Webhook module.
   */
  renderExtras?: (moduleId: string) => React.ReactNode;
  /**
   * Per-module warning text. When a module is selected and its
   * dependency isn't met (e.g. Inbound needs Dedicated CLI), return
   * a string here — it renders inside the card as a yellow notice.
   */
  getWarning?: (moduleId: string) => string | null | undefined;
  /** Disable the entire grid. */
  disabled?: boolean;
}

/**
 * Card-grid module picker. Each module is a self-contained card with
 * an icon, name, description, a Switch, and (when selected) an inline
 * panel with its conditional fields. Replaces the legacy multiselect-
 * dropdown UI which forced users to map dropdown picks to scattered
 * extra fields elsewhere on the page.
 *
 * The cards are click-to-toggle (anywhere on the card), with the
 * Switch as a visible-state indicator. Extras and warnings live INSIDE
 * the card so all context for a module is co-located.
 */
export function ModuleSelector({
  options,
  value,
  onChange,
  renderExtras,
  getWarning,
  disabled,
}: ModuleSelectorProps) {
  const selectedSet = new Set(value);

  const toggle = (id: string) => {
    if (disabled) return;
    if (selectedSet.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  if (options.length === 0) {
    return (
      <div className="rounded-md border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
        No modules available to assign.
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {options.map((opt) => {
        const selected = selectedSet.has(opt.value);
        const Icon = opt.icon;
        const warning = selected ? getWarning?.(opt.value) : null;
        return (
          <div
            key={opt.value}
            className={cn(
              'group/module flex flex-col rounded-lg border bg-card transition-all',
              selected ? 'border-primary shadow-sm ring-1 ring-primary/30' : 'hover:border-ring',
              disabled && 'opacity-50',
            )}
          >
            {/*
             * Card header — a div (not a button) so the Switch inside is
             * not nested inside another button. Nested buttons are invalid
             * HTML and cause the browser to re-dispatch clicks, which under
             * controlled form state can flip the value in a render-loop.
             */}
            <div
              role="button"
              tabIndex={disabled ? -1 : 0}
              onClick={() => toggle(opt.value)}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle(opt.value);
                }
              }}
              aria-pressed={selected}
              aria-disabled={disabled}
              className={cn(
                'flex w-full items-start gap-3 rounded-lg p-4 text-left',
                !disabled && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              )}
            >
              {Icon && (
                <span
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-md transition-colors',
                    selected
                      ? 'bg-primary/15 text-primary'
                      : 'bg-muted text-muted-foreground group-hover/module:bg-muted/70',
                  )}
                  aria-hidden
                >
                  {selected ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'text-sm font-medium',
                      selected ? 'text-foreground' : 'text-foreground/85',
                    )}
                  >
                    {opt.label}
                  </span>
                </div>
                {opt.description && (
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                    {opt.description}
                  </p>
                )}
              </div>
              <span
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                className="mt-0.5"
              >
                <Switch
                  checked={selected}
                  onCheckedChange={() => toggle(opt.value)}
                  disabled={disabled}
                  aria-label={`${opt.label} module`}
                />
              </span>
            </div>

            {warning && (
              <div className="mx-4 mb-3 flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-foreground">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
                <span>{warning}</span>
              </div>
            )}

            {selected && renderExtras && (
              <div
                className="space-y-3 border-t bg-muted/30 px-4 py-3"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                {renderExtras(opt.value)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
